import { useState } from 'preact/hooks';
import { route, back, navigate } from '@/state/nav';
import { animals } from '@/state/data';
import { upsertAnimal } from '@/lib/mutations';
import { showToast, openSheet } from '@/state/ui';
import { initials, avatarClass, type AnimalCategory, type AnimalCondition } from '@/types';
import { ScheduleSheet } from '@/components/ScheduleSheet';

const CATEGORIES: { key: AnimalCategory; label: string }[] = [
  { key: 'bird', label: 'Songbird' },
  { key: 'raptor', label: 'Raptor' },
  { key: 'mammal', label: 'Mammal' },
  { key: 'rabbit', label: 'Rabbit' },
  { key: 'reptile', label: 'Reptile' },
  { key: 'other', label: 'Other' },
];
const STAGES = ['Hatchling', 'Nestling', 'Juvenile', 'Sub-adult', 'Adult'];

/** New-animal form; also handles edit mode at /roster/:id?edit=1. */
export function AddAnimalScreen() {
  const r = route.value;
  const editId = r.name === 'roster-detail' && r.query.get('edit') ? r.params.id : null;
  const existing = editId ? animals.value.get(editId) : null;

  const [name, setName] = useState(existing?.name ?? '');
  const [species, setSpecies] = useState(existing?.species ?? '');
  const [category, setCategory] = useState<AnimalCategory>(existing?.category ?? 'bird');
  const [stage, setStage] = useState(existing?.life_stage ?? '');
  const [isBatch, setIsBatch] = useState(existing?.kind === 'batch');
  const [count, setCount] = useState(String(existing?.count ?? 1));
  const [condition, setCondition] = useState<AnimalCondition>(existing?.condition ?? 'stable');
  const [diet, setDiet] = useState(existing?.diet ?? '');
  const [dietNotes, setDietNotes] = useState(existing?.diet_notes ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) {
      showToast({ kind: 'info', message: 'Give them a name or ID first' });
      return;
    }
    setSaving(true);
    const id = await upsertAnimal(
      {
        name: name.trim(),
        species: species.trim() || null,
        category,
        life_stage: stage || null,
        kind: isBatch ? 'batch' : 'individual',
        count: isBatch ? Math.max(2, +count || 2) : 1,
        condition,
        diet: diet.trim() || null,
        diet_notes: dietNotes.trim() || null,
        notes: notes.trim() || null,
        intake_date: existing?.intake_date ?? new Date().toISOString().slice(0, 10),
      },
      editId ?? undefined
    );
    setSaving(false);
    showToast({ kind: 'success', message: editId ? 'Saved' : `${name.trim()} added to the roster` });
    navigate(`/roster/${id}`, true);
    if (!editId) {
      // straight into a schedule so the clock starts
      setTimeout(
        () => openSheet({ title: 'Feeding schedule', content: <ScheduleSheet animalId={id} /> }),
        300
      );
    }
  };

  return (
    <div class="screen screen--no-tabbar screen--fade-in">
      <div class="form-topbar">
        <button class="btn btn--ghost btn--sm" onClick={() => back('/roster')}>
          Cancel
        </button>
        <h2 class="t-h3">{editId ? `Edit ${existing?.name ?? ''}` : 'New animal'}</h2>
        <button
          class="btn btn--ghost btn--sm"
          style={{ color: 'var(--brand-primary)', fontWeight: 700 }}
          onClick={save}
          disabled={saving}
        >
          Save
        </button>
      </div>

      <div class="form-scroll">
        <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
          <span class={`avatar avatar--xl ${avatarClass(category)}`}>
            {name ? initials(name) : '?'}
          </span>
          <p class="t-meta" style={{ margin: 0 }}>
            Photos attach from the camera once they're on the roster.
          </p>
        </div>

        <div>
          <label class="label" for="an-name">Name or ID</label>
          <input id="an-name" class="input" value={name} placeholder="Pebble, #2026-114…"
            onInput={(e) => setName((e.target as HTMLInputElement).value)} />
        </div>

        <div>
          <label class="label" for="an-species">Species</label>
          <input id="an-species" class="input" value={species} placeholder="American robin"
            onInput={(e) => setSpecies((e.target as HTMLInputElement).value)} />
        </div>

        <div>
          <span class="label">Type</span>
          <div class="chip-row">
            {CATEGORIES.map((c) => (
              <button key={c.key} class={`pill ${category === c.key ? 'pill--accent' : ''}`} onClick={() => setCategory(c.key)}>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <span class="label">Life stage</span>
          <div class="seg-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
            {STAGES.map((s) => (
              <button key={s} class={`seg ${stage === s ? 'is-on' : ''}`} style={{ fontSize: '11px' }}
                onClick={() => setStage(stage === s ? '' : s)}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-3)' }}>
          <span class={`switch ${isBatch ? 'is-on' : ''}`} role="switch" aria-checked={isBatch}
            onClick={() => setIsBatch(!isBatch)} />
          <span class="t-small">This is a batch (several animals fed together)</span>
        </label>
        {isBatch && (
          <div>
            <label class="label" for="an-count">How many?</label>
            <input id="an-count" class="input" type="number" inputMode="numeric" min="2" value={count}
              onInput={(e) => setCount((e.target as HTMLInputElement).value)} />
          </div>
        )}

        <div>
          <span class="label">Condition</span>
          <div class="seg-grid seg-grid--3">
            {(['stable', 'watch', 'critical'] as const).map((c) => (
              <button key={c} class={`seg ${condition === c ? 'is-on' : ''}`}
                style={condition === c && c !== 'stable' ? { background: c === 'critical' ? 'var(--brand-alert)' : 'var(--brand-tertiary)' } : undefined}
                onClick={() => setCondition(c)}>
                {c[0].toUpperCase() + c.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label class="label" for="an-diet">Diet</label>
          <input id="an-diet" class="input" value={diet} placeholder="Mealworms, soaked kibble"
            onInput={(e) => setDiet((e.target as HTMLInputElement).value)} />
        </div>
        <div>
          <label class="label" for="an-dietnotes">Diet notes</label>
          <input id="an-dietnotes" class="input" value={dietNotes} placeholder="¼ cup per feed; hand-feed if reluctant"
            onInput={(e) => setDietNotes((e.target as HTMLInputElement).value)} />
        </div>

        <div>
          <label class="label" for="an-notes">Intake notes</label>
          <textarea id="an-notes" class="textarea" value={notes}
            placeholder="Where found, condition, ID number…"
            onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)} />
        </div>
      </div>
    </div>
  );
}
